import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import LoadingSpinner from '../components/LoadingSpinner';

import {
  fetchDepartments,
  addDepartment,
  updateDepartment,
  fetchCompany,
  toggleDepartmentStatus,
} from "../services/apiService";

export default function DepartmentPage() {
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [editingDept, setEditingDept] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    department_name: "",
    company: "",
    is_active: true,
  });

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        await Promise.all([loadDepartments(), loadCompanies()]);
      } finally {
        setIsPageLoading(false);
      }
    };
    loadInitialData();
  }, []);

  const loadDepartments = async () => {
    try {
      const data = await fetchDepartments();
      setDepartments(data);
    } catch (err) {
      console.error("Failed to fetch departments", err);
      toast.error("Failed to load departments.");
    }
  };

  const loadCompanies = async () => {
    try {
      const data = await fetchCompany();
      setCompanies(data);
    } catch (err) {
      console.error("Failed to fetch companies", err);
      toast.error("Failed to load companies.");
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async () => {
    const { department_name, company, is_active } = formData;

    if (!department_name.trim()) {
      toast.warn("Department name is required.");
      return;
    }

    if (!company) {
      toast.warn("Please select a company.");
      return;
    }

    try {
      if (editingDept) {
        await updateDepartment({
          department_id: editingDept.department_id,
          department_name,
          company,
          is_active,
        });
        toast.success("Department updated successfully.");
      } else {
        await addDepartment({ 
          department_name, 
          company, 
          is_active: true
        });
        toast.success("Department added successfully.");
      }

      setFormData({ department_name: "", company: "", is_active: true });
      setEditingDept(null);
      setShowModal(false);
      loadDepartments();
    } catch (err) {
      console.error("Error saving department", err);
      toast.error("Failed to save department.");
    }
  };

  const handleEdit = (dept) => {
    setEditingDept(dept);
    setFormData({
      department_name: dept.department_name,
      company: dept.company || "",
      is_active: dept.isActive !== undefined ? dept.isActive : true,
    });
    setShowModal(true);
  };

  const handleAddNew = () => {
    setEditingDept(null);
    setFormData({ department_name: "", company: "", is_active: true });
    setShowModal(true);
  };

  const getDepartmentStatus = (dept) => {
    return dept.isActive !== undefined ? dept.isActive : true;
  };

  const handleToggleStatus = async (dept) => {
    try {
      const currentStatus = getDepartmentStatus(dept);
      await toggleDepartmentStatus(dept.department_id, !currentStatus);
      toast.success(`Department ${currentStatus ? "deactivated" : "activated"} successfully.`);
      loadDepartments();
    } catch (err) {
      console.error("Error toggling department status", err);
      toast.error("Failed to update department status.");
    }
  };

  if (isPageLoading) return <LoadingSpinner label="Loading Departments" />;

  return (
    <div className="flex flex-col 2xl:px-[10%] pt-8 bg-white xl:pt-24 md:pt-8 md:rounded-t-[40px] xl:rounded-t-[48px] mx-4 h-[70vh] sm:h-[72vh] md:h-[74.5vh] lg:h-[74.5vh] xl:px-[calc(8%-1rem)] 3xl:px-[calc(8%-1rem)] 4xl:px-[calc(9.5%-1rem)] md:px-[calc(5%-1rem)]">
      <div className='bg-white rounded-2xl p-6 filter drop-shadow-[0_5px_30px_rgba(10,60,96,0.10)] h-[70vh] sm:h-[65vh] md:h-[68vh] lg:h-[63vh] xl:h-[90vh] overflow-y-auto relative xl:-top-[160px]'>
        <div className="flex flex-col sm:flex-col md:flex-row md:items-center md:gap-4 xl:gap-6 w-full">
          <h3 className="text-xl sm:text-2xl text-primary-800 font-semibold mb-4 md:mb-0">
            Department Details
          </h3>

          <div className="flex flex-wrap sm:gap-4 md:flex-nowrap md:gap-4 ml-auto w-full md:w-auto">
            <button
              onClick={handleAddNew}
              className="w-full sm:w-auto md:w-auto bg-primary-800 text-white px-6 py-2 rounded-xl text-base xl:text-lg hover:bg-opacity-90 transition-colors"
            >
              + Add New Department
            </button>
          </div>
        </div>

        <div className="overflow-x-auto pt-6">
          <table className="min-w-full table-auto border-collapse">
            <thead className="bg-[#f5f3ef] text-primary-600 py-2 px-2 border-r border-primary-500/20 bg-primary-800/10 text-primary-800 text-left">
              <tr>
                <th className="py-3 px-4 border-r border-primary-500/20">Department Name</th>
                <th className="py-3 px-4 border-r border-primary-500/20">Company</th>
                <th className="py-3 px-4 border-r border-primary-500/20">Status</th>
                <th className="py-3 px-4 border-r border-primary-500/20">Actions</th>
              </tr>
            </thead>
            <tbody>
              {departments.map((dept, index) => {
                const isActive = getDepartmentStatus(dept);
                return (
                  <tr
                    key={dept.department_id}
                    className={`${
                      index % 2 === 0 ? "bg-white" : "bg-primary-800/10"
                    } hover:bg-gray-50 transition border-b text-sm text-primary-600`}
                  >
                    <td className="px-4 py-2 border-r text-[#232323]">{dept.department_name}</td>
                    <td className="px-4 py-2 border-r text-[#232323]">{dept.company}</td>
                    <td className="px-4 py-2 border-r">
                      <button
                        onClick={() => handleToggleStatus(dept)}
                        className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                          isActive
                            ? "bg-green-100 text-green-800 hover:bg-green-200"
                            : "bg-red-100 text-red-800 hover:bg-red-200"
                        }`}
                      >
                        {isActive ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className="px-4 py-2 border text-center">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(dept)}
                          className="bg-primary-800/30 p-2 rounded-xl flex items-center cursor-pointer hover:bg-primary-800 text-primary-800 hover:text-white transition-colors"
                        >
                          <span className='text-sm'>Edit</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {departments.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center text-gray-500 py-4">
                    No departments found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal with matching design */}
      {showModal && (
        <div className="fixed inset-0 backdrop-blur-sm flex justify-center items-center z-50 p-4" style={{ backgroundColor: 'rgba(18, 76, 114, 0.2)' }}>
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl lg:max-w-3xl" style={{ borderColor: 'rgba(18, 76, 114, 0.1)', borderWidth: '1px' }}>
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b rounded-t-2xl" style={{ 
              borderColor: 'rgba(18, 76, 114, 0.1)',
              background: 'linear-gradient(to right, rgba(18, 76, 114, 0.05), transparent)'
            }}>
              <h2 className="text-xl font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>
                {editingDept ? 'Edit Department' : 'Add New Department'}
              </h2>
              <button 
                onClick={() => setShowModal(false)} 
                className="transition-all rounded-full p-1.5"
                style={{ color: 'rgba(18, 76, 114, 0.6)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgb(18, 76, 114)';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = 'rgba(18, 76, 114, 0.6)';
                }}
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <div className="space-y-6">
                {/* Form Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* Department Name */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
                      Department Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="department_name"
                      value={formData.department_name}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border rounded-lg outline-none transition text-gray-700"
                      style={{ 
                        borderColor: 'rgba(18, 76, 114, 0.3)',
                        backgroundColor: 'white'
                      }}
                      onFocus={(e) => {
                        e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)';
                        e.target.style.borderColor = 'rgb(18, 76, 114)';
                      }}
                      onBlur={(e) => {
                        e.target.style.boxShadow = 'none';
                        e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)';
                      }}
                      placeholder="Enter department name"
                    />
                  </div>

                  {/* Company */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
                      Company <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border rounded-lg outline-none transition text-gray-700"
                      style={{ 
                        borderColor: 'rgba(18, 76, 114, 0.3)',
                        backgroundColor: 'white'
                      }}
                      onFocus={(e) => {
                        e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)';
                        e.target.style.borderColor = 'rgb(18, 76, 114)';
                      }}
                      onBlur={(e) => {
                        e.target.style.boxShadow = 'none';
                        e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)';
                      }}
                    >
                      <option value="">Select Company</option>
                      {companies.map((company) => (
                        <option key={company.companyCode} value={company.companyCode}>
                          {company.companyName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Active Status Checkbox */}
                {editingDept && (
                  <div className="space-y-2">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <div className="relative">
                        <input
                          type="checkbox"
                          id="is_active"
                          name="is_active"
                          checked={formData.is_active}
                          onChange={handleChange}
                          className="sr-only"
                        />
                        <div 
                          className={`w-6 h-6 rounded border flex items-center justify-center transition-colors ${formData.is_active ? 'bg-blue-500 border-blue-500' : 'bg-white border-gray-400'}`}
                        >
                          {formData.is_active && (
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                      <span className="text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
                        Active Department
                      </span>
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex flex-col sm:flex-row justify-end gap-3 p-6 border-t rounded-b-2xl" style={{ 
              borderColor: 'rgba(18, 76, 114, 0.1)',
              background: 'linear-gradient(to right, transparent, rgba(18, 76, 114, 0.05))'
            }}>
              <button
                onClick={() => setShowModal(false)}
                className="px-5 py-2.5 bg-white border rounded-lg transition-all font-medium"
                style={{ 
                  color: 'rgb(18, 76, 114)',
                  borderColor: 'rgba(18, 76, 114, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(18, 76, 114, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="px-5 py-2.5 text-white font-medium rounded-lg transition-all"
                style={{ 
                  backgroundColor: 'rgb(18, 76, 114)',
                  cursor: 'pointer',
                  boxShadow: '0 10px 15px -3px rgba(18, 76, 114, 0.2)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(18, 76, 114, 0.9)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgb(18, 76, 114)';
                }}
              >
                {editingDept ? 'Update Department' : 'Add Department'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}